import {
	Center,
	CloseButton,
	Group,
	NumberInput,
	Table,
	TextInput,
	Tooltip,
} from '@mantine/core';
import {
	IconCancel,
	IconCheck,
	IconCircleDashedMinus,
	IconCircleDashedPlus,
	IconEdit,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { formatToCurrency, formatToPercentage } from '../../../util/formatting';
import { useEditDescription, useEditRate } from '../hooks/useEditVatRateForm';
import type { VatRateDto } from '../types/VatRateDto';
import { KeyedRow } from '../../../components/KeyedRow';

export function EditableVatRow({
	initialVatRate,
}: { initialVatRate: VatRateDto }) {
	const { organisation } = useParams();

	const [isEditMode, setIsEditMode] = useState(false);

	const [currentVatRate, setCurrentVatRate] = useState(initialVatRate);

	const vatRateMutation = mutate<VatRateDto>(
		`${organisation}/vat-rates/edit`,
		currentVatRate,
	);

	const rateField = useEditRate(currentVatRate.rate);
	const descriptionField = useEditDescription(currentVatRate.description);

	const isRateEmpty = useMemo(() => {
		return rateField.form.getValue() < 0.00000001;
	}, [rateField.form]);

	const isRateToLarge = useMemo(() => {
		return rateField.form.getValue() > 100;
	}, [rateField.form]);

	const toggleEditMode = () => {
		if (isEditMode) {
			rateField.form.reset();
			descriptionField.form.reset();
			setIsEditMode(false);
		} else {
			setIsEditMode(true);
		}
	};

	const save = () => {
		const vatRate = currentVatRate;
		vatRate.description = descriptionField.form.getValue();
		vatRate.rate = rateField.form.getValue();

		setCurrentVatRate(vatRate);

		vatRateMutation.mutate();
		setIsEditMode(false);
	};

	return (
		<>
			<KeyedRow h={'7vh'} px={10}>
				<Table.Td>
					<Center>
						{isRateEmpty ? (
							<IconCircleDashedMinus {...defaultIconStyle} />
						) : (
							<IconCircleDashedPlus {...defaultIconStyle} color="green" />
						)}
					</Center>
				</Table.Td>
				<Table.Td>{currentVatRate.id}</Table.Td>
				{isEditMode ? (
					<>
						<Table.Td>
							<TextInput
								maxLength={30}
								{...descriptionField.form.getInputProps()}
							/>
						</Table.Td>
						<Table.Td>
							<Tooltip
								label={'You cannot insert a number greater than 100'}
								disabled={isRateToLarge}
							>
								<NumberInput
									allowNegative={false}
									allowDecimal
									decimalScale={2}
									max={100}
									suffix="%" 
									clampBehavior="strict"
									withErrorStyles={isRateToLarge}
									{...rateField.form.getInputProps()}
								/>
							</Tooltip>
						</Table.Td>
					</>
				) : (
					<>
						<Table.Td>{currentVatRate.description}</Table.Td>
						<Table.Td>{formatToPercentage(currentVatRate.rate / 100)}</Table.Td>
					</>
				)}
				<Table.Td>
					<Group justify={'center'}>
						{formatToCurrency(100 - currentVatRate.rate)}
					</Group>
				</Table.Td>
				<Table.Td>
					<Group justify={'center'}>
						<CloseButton
							icon={
								!isEditMode ? (
									<IconEdit {...defaultIconStyle} />
								) : (
									<IconCancel {...defaultIconStyle} />
								)
							}
							onClick={() => toggleEditMode()}
						/>
						{isEditMode && (
							<CloseButton
								icon={<IconCheck {...defaultIconStyle} />}
								onClick={() => save()}
							/>
						)}
					</Group>
				</Table.Td>
			</KeyedRow>
		</>
	);
}
